import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, ArrowLeft, Layout, FileText, Home, Users, Calendar, Briefcase, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { landingPagesApi } from '@/api/landingPages';
import TrackingFields, { emptyTracking, trackingPayload } from '@/components/TrackingFields';
import { useRegionRequired } from '@/lib/partner';

const TEMPLATE_ICONS = {
  property_listing: Home,
  agent_profile: Users,
  open_house: Calendar,
  general_marketing: Briefcase,
  contact_only: FileText,
  blank: Layout,
  custom: Sparkles,
  real_estate: Home,
  business: Briefcase,
  events: Calendar,
  marketing: Sparkles,
};

export default function CreatePage() {
  const regionRequired = useRegionRequired();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedTemplateId = searchParams.get('template');
  
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [tracking, setTracking] = useState(emptyTracking);

  useEffect(() => {
    fetchTemplates();
  }, []);

  useEffect(() => {
    // Preselect template if ID is provided in URL
    if (preselectedTemplateId && templates.length > 0) {
      const template = templates.find(t => t.id === preselectedTemplateId);
      if (template) {
        setSelectedTemplate(template);
      }
    }
  }, [preselectedTemplateId, templates]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const data = await landingPagesApi.getTemplates();
      if (data.success) {
        setTemplates(data.data || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!pageTitle.trim()) {
      toast.error('Please enter a page title');
      return;
    }
    if (regionRequired && !tracking.region_id) {
      toast.error('Please select a region before creating this page.');
      return;
    }

    setCreating(true);
    try {
      const data = await landingPagesApi.create({
        title: pageTitle.trim(),
        template_type: selectedTemplate?.type || 'blank',
        template_id: selectedTemplate?.id,
        tracking: trackingPayload(tracking),
      });
      
      if (data.success) {
        toast.success('Page created!');
        navigate(`/pages/${data.data.id}`);
      } else {
        toast.error(data.detail || 'Failed to create page');
      }
    } catch (error) {
      console.error('Error creating page:', error);
      toast.error('Failed to create page');
    } finally {
      setCreating(false);
    }
  };

  const getTemplateIcon = (template) => {
    const Icon = TEMPLATE_ICONS[template.type] || TEMPLATE_ICONS[template.category] || Layout;
    return Icon;
  };

  // Group templates by type (system vs custom)
  const systemTemplates = templates.filter(t => t.is_system);
  const customTemplates = templates.filter(t => !t.is_system);

  return (
    <div className="space-y-6" data-testid="create-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/pages')}>
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-semibold">Create Landing Page</h1>
          <p className="text-muted-foreground">Choose a template and give your page a name</p>
        </div>
      </div>

      {/* Page Title Input */}
      <Card>
        <CardHeader>
          <CardTitle>Page Details</CardTitle>
          <CardDescription>Enter a title for your new landing page</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="title">Page Title *</Label>
            <Input
              id="title"
              placeholder="e.g., Property Showcase - 123 Main Street"
              value={pageTitle}
              onChange={(e) => setPageTitle(e.target.value)}
              data-testid="page-title-input"
            />
          </div>
        </CardContent>
      </Card>

      {/* Organization linkage */}
      <Card>
        <CardHeader>
          <CardTitle>Organization</CardTitle>
          <CardDescription>Who this page belongs to</CardDescription>
        </CardHeader>
        <CardContent>
          <TrackingFields value={tracking} onChange={setTracking} />
        </CardContent>
      </Card>

      {/* Template Selection */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {[...Array(8)].map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-12 w-12 rounded-lg mb-4" />
                <Skeleton className="h-5 w-2/3 mb-2" />
                <Skeleton className="h-4 w-full" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <>
          {/* System Templates */}
          {systemTemplates.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">System Templates</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {systemTemplates.map((template) => {
                  const Icon = getTemplateIcon(template);
                  const isSelected = selectedTemplate?.id === template.id;
                  return (
                    <Card
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        isSelected 
                          ? 'ring-2 ring-primary border-primary bg-primary/5' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <CardContent className="pt-6">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="font-medium mb-1">{template.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {template.description || 'No description'}
                        </p>
                        <div className="flex gap-2 mt-3">
                          <Badge variant="secondary" className="text-xs">
                            {template.sections?.length || 0} sections
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Custom Templates */}
          {customTemplates.length > 0 && (
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Custom Templates</h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {customTemplates.map((template) => {
                  const Icon = getTemplateIcon(template);
                  const isSelected = selectedTemplate?.id === template.id;
                  return (
                    <Card
                      key={template.id}
                      onClick={() => setSelectedTemplate(template)}
                      className={`cursor-pointer transition-all hover:shadow-md ${
                        isSelected 
                          ? 'ring-2 ring-primary border-primary bg-primary/5' 
                          : 'hover:border-primary/50'
                      }`}
                      data-testid={`template-${template.id}`}
                    >
                      <CardContent className="pt-6">
                        <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                          isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted'
                        }`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <h3 className="font-medium mb-1">{template.name}</h3>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {template.description || 'No description'}
                        </p>
                        <div className="flex gap-2 mt-3">
                          <Badge variant="outline" className="text-xs">
                            {template.category}
                          </Badge>
                          <Badge variant="secondary" className="text-xs">
                            {template.sections?.length || 0} sections
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          )}

          {/* Blank Template */}
          <div className="space-y-4">
            <h2 className="text-lg font-semibold">Start from Scratch</h2>
            <Card
              onClick={() => setSelectedTemplate(null)}
              className={`cursor-pointer transition-all hover:shadow-md max-w-xs ${
                selectedTemplate === null 
                  ? 'ring-2 ring-primary border-primary bg-primary/5' 
                  : 'hover:border-primary/50'
              }`}
              data-testid="template-blank"
            >
              <CardContent className="pt-6">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mb-4 ${
                  selectedTemplate === null ? 'bg-primary text-primary-foreground' : 'bg-muted'
                }`}>
                  <Layout className="w-6 h-6" />
                </div>
                <h3 className="font-medium mb-1">Blank Page</h3>
                <p className="text-sm text-muted-foreground">
                  Start with an empty page and build from scratch
                </p>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Create Button */}
      <div className="flex justify-end gap-4 pt-4 border-t">
        <Button variant="outline" onClick={() => navigate('/pages')}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          disabled={creating || !pageTitle.trim() || (regionRequired && !tracking.region_id)}
          data-testid="create-page-btn"
        >
          {creating ? 'Creating...' : 'Create Page'}
        </Button>
      </div>
    </div>
  );
}
